import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";

const links = [
  { label: "Products", to: "/products" },
  { label: "Services", to: "/#services" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Blog", to: "/blog" },
  { label: "About", to: "/#about" },
  { label: "Contact", to: "/#contact" },
];

export const Nav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isActive = (to) => {
    if (to.startsWith("/#")) return false;
    return pathname === to || (to !== "/" && pathname.startsWith(to));
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12 h-16 flex items-center justify-between">
        <Logo />

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <Link
              key={l.label}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`text-sm font-medium transition-colors ${
                isActive(l.to)
                  ? "text-[#002FA7] dark:text-[#7C9BFF]"
                  : "text-zinc-700 hover:text-[#002FA7] dark:text-zinc-300 dark:hover:text-white"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle />
          <Link
            to="/#contact"
            data-testid="nav-cta-demo"
            className="group inline-flex items-center gap-1.5 bg-zinc-950 text-white px-4 py-2 rounded-sm text-sm font-medium hover:bg-[#002FA7] dark:bg-white dark:text-zinc-950 dark:hover:bg-[#002FA7] dark:hover:text-white transition-colors"
          >
            Book Demo
            <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle />
          <button
            aria-label="Toggle menu"
            data-testid="nav-toggle"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex items-center justify-center w-9 h-9 rounded-sm border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-white border-t border-zinc-200 dark:bg-zinc-950 dark:border-zinc-800" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.label}
                to={l.to}
                onClick={() => setOpen(false)}
                className="py-2 text-zinc-800 font-medium dark:text-zinc-200"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              className="mt-2 bg-zinc-950 text-white text-center py-2.5 rounded-sm font-medium dark:bg-white dark:text-zinc-950"
            >
              Book Demo
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Nav;